import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Header from '../components/Header';
import {
  Swords, Loader2, Coins, Gift, History as HistoryIcon, Trophy, X, Search,
  Atom, FlaskConical, Calculator, Leaf, Zap,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SUBJECT_META = {
  Physics:     { Icon: Atom,        gradient: 'from-blue-500 to-cyan-500' },
  Chemistry:   { Icon: FlaskConical, gradient: 'from-emerald-500 to-green-500' },
  Mathematics: { Icon: Calculator,  gradient: 'from-purple-500 to-fuchsia-500' },
  Biology:     { Icon: Leaf,        gradient: 'from-rose-500 to-pink-500' },
};

const HeadToHead = () => {
  const navigate = useNavigate();
  const [topics, setTopics] = useState([]);
  const [entryFee, setEntryFee] = useState(20);
  const [prize, setPrize] = useState(36);
  const [search, setSearch] = useState('');
  const [mode, setMode] = useState('free');         // 'free' | 'ranked'
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [joining, setJoining] = useState(false);
  const [waitingMatch, setWaitingMatch] = useState(null);
  const [history, setHistory] = useState([]);
  const pollRef = useRef(null);

  const fetchTopics = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/h2h/topics`);
      setTopics(data.topics);
      setEntryFee(data.entry_fee_ranked);
      setPrize(data.prize_winner_ranked);
    } catch (e) { /* silent */ }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/h2h/history`);
      setHistory(data);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => { fetchTopics(); fetchHistory(); }, [fetchTopics, fetchHistory]);

  // Poll waiting match for opponent to join
  useEffect(() => {
    if (!waitingMatch || waitingMatch.status !== 'waiting') {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/h2h/match/${waitingMatch.id}`);
        if (data.status === 'active') {
          toast.success('Opponent found! Starting match…');
          navigate(`/h2h/match/${data.id}`, { state: { match: data } });
        } else if (data.status === 'cancelled') {
          setWaitingMatch(null);
        }
      } catch (e) { /* silent */ }
    }, 2500);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [waitingMatch, navigate]);

  // Free-mode bot fallback: after 30s without a real opponent, ask backend to pair with a bot.
  useEffect(() => {
    if (!waitingMatch || waitingMatch.status !== 'waiting' || waitingMatch.mode !== 'free') return;
    const timer = setTimeout(async () => {
      try {
        const { data } = await axios.post(`${API_URL}/api/h2h/match/${waitingMatch.id}/spawn-bot`);
        if (data.status === 'active') {
          const opponent = data.you_are === 'player1' ? data.player2?.name : data.player1?.name;
          toast.success(`Matched with ${opponent || 'an opponent'}!`);
          navigate(`/h2h/match/${data.id}`, { state: { match: data } });
        }
      } catch (e) { /* silent — user may have already cancelled */ }
    }, 30000);
    return () => clearTimeout(timer);
  }, [waitingMatch, navigate]);

  const joinQueue = async () => {
    if (!selectedTopic) { toast.error('Pick a topic first'); return; }
    setJoining(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/h2h/queue`, { mode, topic_id: selectedTopic.id });
      if (data.status === 'active') {
        toast.success('Opponent found!');
        navigate(`/h2h/match/${data.id}`, { state: { match: data } });
      } else {
        setWaitingMatch(data);
        toast.info('Looking for opponent…', { duration: 3000 });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to join queue');
    } finally {
      setJoining(false);
    }
  };

  const cancelMatch = async () => {
    if (!waitingMatch) return;
    try {
      await axios.post(`${API_URL}/api/h2h/match/${waitingMatch.id}/cancel`);
      toast.success(mode === 'ranked' ? 'Cancelled. Entry fee refunded.' : 'Cancelled');
      setWaitingMatch(null);
    } catch (e) {
      toast.error('Failed to cancel');
    }
  };

  const filteredTopics = topics.filter(t =>
    !search || t.label.toLowerCase().includes(search.toLowerCase()) || t.subject.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="min-h-screen" data-testid="h2h-page">
      <Header />
      <main className="max-w-6xl mx-auto px-6 py-10">
        {/* Hero */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-rose-100 to-orange-100 border border-rose-200 mb-4">
            <Swords className="w-4 h-4 text-rose-700" />
            <span className="text-xs font-bold text-rose-700 uppercase tracking-wider">1v1 Head-to-Head</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-black text-[#111827] mb-3" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            Battle Live. <span className="bg-gradient-to-r from-rose-500 via-orange-500 to-amber-500 bg-clip-text text-transparent">10 Q. 5 min.</span>
          </h1>
          <p className="text-base text-[#6B7280] max-w-xl mx-auto">
            Pick a topic. Get auto-matched with another aspirant. Same questions, same clock — fastest accurate wins.
          </p>
        </div>

        {/* Mode toggle + rules */}
        <div className="quiz-card p-6 mb-8" data-testid="mode-selector">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              onClick={() => setMode('free')}
              data-testid="mode-free"
              className={`text-left p-5 rounded-2xl border-2 transition-all ${
                mode === 'free' ? 'border-[#0EA5E9] bg-cyan-50 shadow-md' : 'border-slate-200 hover:border-slate-300'
              }`}
            >
              <div className="flex items-center gap-2 mb-1.5">
                <Zap className="w-4 h-4 text-emerald-600" />
                <span className="text-xs font-black uppercase tracking-widest text-emerald-700">Free Practice</span>
              </div>
              <h3 className="text-lg font-black text-[#111827]" style={{ fontFamily: 'Fredoka, sans-serif' }}>No-stakes warm-up</h3>
              <p className="text-xs text-[#6B7280] mt-1">Play unlimited. Builds your streak. No points at risk.</p>
            </button>
            <button
              onClick={() => setMode('ranked')}
              data-testid="mode-ranked"
              className={`text-left p-5 rounded-2xl border-2 transition-all ${
                mode === 'ranked' ? 'border-amber-500 bg-amber-50 shadow-md' : 'border-slate-200 hover:border-slate-300'
              }`}
            >
              <div className="flex items-center gap-2 mb-1.5">
                <Trophy className="w-4 h-4 text-amber-600" />
                <span className="text-xs font-black uppercase tracking-widest text-amber-700">Ranked Match</span>
              </div>
              <h3 className="text-lg font-black text-[#111827]" style={{ fontFamily: 'Fredoka, sans-serif' }}>Stake {entryFee} → Win {prize}</h3>
              <p className="text-xs text-[#6B7280] mt-1">{entryFee} pts entry. Winner takes {prize} pts. Draws refund both.</p>
            </button>
          </div>
        </div>

        {/* Topic search */}
        <div className="mb-5 relative">
          <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search topics — Physics, Calculus, Genetics…"
            className="w-full pl-11 pr-4 py-3 rounded-2xl border-2 border-slate-200 focus:border-[#0EA5E9] outline-none transition bg-white"
            data-testid="topic-search-input"
          />
        </div>

        {/* Topic grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-8" data-testid="topic-grid">
          {filteredTopics.map((t) => {
            const meta = SUBJECT_META[t.subject] || SUBJECT_META.Physics;
            const Icon = meta.Icon;
            const isSelected = selectedTopic?.id === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setSelectedTopic(t)}
                data-testid={`topic-${t.id}`}
                className={`text-left p-4 rounded-2xl transition-all relative ${
                  isSelected
                    ? 'ring-2 ring-[#0EA5E9] shadow-lg -translate-y-0.5 bg-white'
                    : 'bg-white border border-slate-100 hover:shadow-md hover:-translate-y-0.5'
                }`}
              >
                <div className={`w-10 h-10 rounded-2xl bg-gradient-to-br ${meta.gradient} flex items-center justify-center shadow-md mb-3`}>
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">{t.subject}</p>
                <h3 className="text-sm font-black text-[#111827] leading-snug" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                  {t.label}
                </h3>
              </button>
            );
          })}
        </div>

        {/* Sticky action CTA */}
        <div className="sticky bottom-4 z-20">
          <div className="quiz-card p-4 flex flex-wrap items-center justify-between gap-3 shadow-lg" data-testid="action-bar">
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-slate-500">Ready to battle?</p>
              <p className="text-sm text-[#111827] font-medium">
                {selectedTopic ? (
                  <>You'll play <span className="font-black">{selectedTopic.label}</span> · {mode === 'free' ? 'Free Practice' : `${entryFee} pts stake`}</>
                ) : (
                  'Pick a topic above to start'
                )}
              </p>
            </div>
            <button
              onClick={joinQueue}
              disabled={!selectedTopic || joining || !!waitingMatch}
              data-testid="find-opponent-btn"
              className="px-6 py-3 rounded-2xl font-bold text-sm flex items-center gap-2 transition-all whitespace-nowrap bg-gradient-to-r from-rose-500 to-orange-500 text-white shadow-[0_4px_0_rgb(180,83,9)] active:translate-y-1 active:shadow-none hover:from-rose-600 hover:to-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {joining ? <><Loader2 className="w-4 h-4 animate-spin" /> Searching…</> : <><Swords className="w-4 h-4" /> Find Opponent</>}
            </button>
          </div>
        </div>

        {/* History */}
        {history.length > 0 && (
          <section className="mt-10" data-testid="h2h-history-section">
            <div className="flex items-center gap-2 mb-4">
              <HistoryIcon className="w-5 h-5 text-[#6B7280]" />
              <h2 className="text-xl font-black text-[#111827]" style={{ fontFamily: 'Fredoka, sans-serif' }}>Recent Battles</h2>
            </div>
            <div className="quiz-card overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-[#6B7280]">
                  <tr>
                    <th className="px-4 py-3 text-left font-bold">Topic</th>
                    <th className="px-4 py-3 text-left font-bold">Mode</th>
                    <th className="px-4 py-3 text-left font-bold">Result</th>
                    <th className="px-4 py-3 text-left font-bold">Score</th>
                    <th className="px-4 py-3 text-left font-bold">When</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((m, idx) => {
                    const youWon = m.winner && m.winner === m.you_are;
                    const tied = m.winner === 'draw';
                    const myScore = m[m.you_are]?.score ?? '—';
                    const oppKey = m.you_are === 'player1' ? 'player2' : 'player1';
                    const oppScore = m[oppKey]?.score ?? '—';
                    return (
                      <tr key={`${m.id}-${idx}`} className="border-t border-slate-100" data-testid={`history-row-${idx}`}>
                        <td className="px-4 py-3 text-[#111827] font-medium">{m.topic_label}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded-md text-xs font-bold ${m.mode === 'ranked' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                            {m.mode}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-black uppercase tracking-wider ${tied ? 'text-slate-500' : youWon ? 'text-emerald-600' : 'text-rose-500'}`}>
                            {tied ? 'Draw' : youWon ? 'Won' : 'Lost'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-[#6B7280]">{myScore} vs {oppScore}</td>
                        <td className="px-4 py-3 text-[#9CA3AF]">{m.completed_at ? new Date(m.completed_at).toLocaleString() : new Date(m.created_at).toLocaleString()}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </main>

      {/* Waiting modal */}
      {waitingMatch && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="waiting-modal">
          <div className="bg-white rounded-3xl p-7 max-w-md w-full shadow-2xl">
            <div className="flex justify-between items-start mb-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-rose-500 to-orange-500 flex items-center justify-center shadow-lg">
                <Loader2 className="w-7 h-7 text-white animate-spin" />
              </div>
              <button onClick={cancelMatch} className="text-slate-400 hover:text-slate-600" data-testid="cancel-waiting"><X className="w-5 h-5" /></button>
            </div>
            <h3 className="text-2xl font-black text-[#111827]" style={{ fontFamily: 'Fredoka, sans-serif' }}>Looking for opponent…</h3>
            <p className="text-sm text-[#6B7280] mt-2">
              Topic: <span className="font-bold text-[#111827]">{waitingMatch.topic_label}</span><br />
              Mode: <span className="font-bold">{waitingMatch.mode === 'ranked' ? `Ranked · ${entryFee} pts at stake` : 'Free Practice'}</span>
            </p>
            <div className="mt-4 p-3 rounded-xl bg-slate-50 text-xs text-slate-600 leading-relaxed">
              {waitingMatch.mode === 'free'
                ? "Searching for a real opponent… if no one joins within 30s, we'll match you with a competitive AI opponent so you can play immediately."
                : `No opponent within 60s? Tap cancel to leave the queue (entry fee will be refunded).`}
            </div>
            <button
              onClick={cancelMatch}
              data-testid="cancel-waiting-btn"
              className="w-full mt-5 px-5 py-3 rounded-2xl font-bold text-sm bg-slate-100 text-slate-700 hover:bg-slate-200 transition-all"
            >
              Cancel & leave queue
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default HeadToHead;
